// ============================================================
// AUTOPILOT DIGITAL — Chat Widget
// Floating support assistant. Used on the dashboard (captain avatar)
// and the marketing site (bubble avatar). Talks to POST /api/chat.
//
// DESIGN: dark/glassmorphism — matches dashboard (accent #31DCBA).
// NOTE: this repo is Create React App (react-scripts), so the API base
// comes from process.env.REACT_APP_BACKEND_URL, matching the rest of
// the codebase. (The task brief mentioned VITE_API_URL, but Vite env
// vars are not available here.)
// ============================================================
import React, { useState, useRef, useEffect } from 'react';
import { authedFetch } from '../lib/authedFetch';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const TEAL = '#31DCBA';

// ── Robot avatar — teal LED eyes, LED smile screen, antenna ──
function CaptainAvatar({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Antenna stick + ball */}
      <line x1="24" y1="5" x2="24" y2="12" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="24" cy="3.5" r="2.5" fill={TEAL} />
      {/* Robot head */}
      <rect x="6" y="12" width="36" height="32" rx="5" fill="#0d1f3d" stroke={TEAL} strokeWidth="1.5" />
      {/* Eye sockets */}
      <rect x="10" y="18" width="12" height="9" rx="2" fill="#060e1f" />
      <rect x="26" y="18" width="12" height="9" rx="2" fill="#060e1f" />
      {/* LED eyes */}
      <rect x="11.5" y="19.5" width="9" height="6" rx="1.5" fill={TEAL} />
      <rect x="27.5" y="19.5" width="9" height="6" rx="1.5" fill={TEAL} />
      {/* LED mouth display */}
      <rect x="12" y="32" width="24" height="7" rx="2" fill="#060e1f" />
      <path d="M13 34 Q24 39 35 34" stroke={TEAL} strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Corner bolts */}
      <circle cx="9" cy="16" r="1.2" fill={TEAL} />
      <circle cx="39" cy="16" r="1.2" fill={TEAL} />
      <circle cx="9" cy="40" r="1.2" fill={TEAL} />
      <circle cx="39" cy="40" r="1.2" fill={TEAL} />
    </svg>
  );
}

// ── Bubble avatar — simple teal chat bubble ──
function BubbleAvatar({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M6 7h24a3 3 0 0 1 3 3v13a3 3 0 0 1-3 3H15l-7 6v-6H6a3 3 0 0 1-3-3V10a3 3 0 0 1 3-3Z"
        fill={TEAL}
      />
      <circle cx="13" cy="16.5" r="1.8" fill="#0d0f14" />
      <circle cx="19" cy="16.5" r="1.8" fill="#0d0f14" />
      <circle cx="25" cy="16.5" r="1.8" fill="#0d0f14" />
    </svg>
  );
}

// ── Chat bubble icon for the collapsed pill ──
function ChatPillIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M4 5h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-8l-5 4v-4H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        fill="#0d0f14"
      />
    </svg>
  );
}

function TypingDots() {
  return (
    <span className="apcw-typing" aria-label="Assistant is typing">
      <span className="apcw-dot" />
      <span className="apcw-dot" />
      <span className="apcw-dot" />
    </span>
  );
}

export default function ChatWidget({ context = 'dashboard', avatarType = 'captain' }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const Avatar = avatarType === 'bubble' ? BubbleAvatar : CaptainAvatar;

  // Auto-scroll to the newest message / typing indicator.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  // Focus the input when the window opens.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      const res = await authedFetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages, context }),
      });
      const data = await res.json().catch(() => ({}));
      const reply = res.ok && data.reply
        ? data.reply
        : "Sorry — I couldn't reach support just now. Please try again in a moment.";
      setMessages([...nextMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages([...nextMessages, {
        role: 'assistant',
        content: "Sorry — I couldn't reach support just now. Please try again in a moment.",
      }]);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div className="apcw-root">
      <style>{styles}</style>

      {/* Expanded chat window */}
      <div className={`apcw-window${open ? ' apcw-open' : ''}`} role="dialog" aria-label="Autopilot Support chat">
        <div className="apcw-header">
          <div className="apcw-header-avatar">
            <Avatar size={avatarType === 'bubble' ? 30 : 34} />
          </div>
          <div className="apcw-header-title">Autopilot Support</div>
          <button className="apcw-close" onClick={() => setOpen(false)} aria-label="Close chat">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        <div className="apcw-messages" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="apcw-msg apcw-assistant">
              Hi! 👋 How can I help you today?
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`apcw-msg ${m.role === 'user' ? 'apcw-user' : 'apcw-assistant'}`}>
              {m.content}
            </div>
          ))}
          {sending && (
            <div className="apcw-msg apcw-assistant apcw-typing-bubble">
              <TypingDots />
            </div>
          )}
        </div>

        <div className="apcw-input-row">
          <input
            ref={inputRef}
            className="apcw-input"
            type="text"
            placeholder="Type your message…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button
            className="apcw-send"
            onClick={send}
            disabled={!input.trim() || sending}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d0f14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Collapsed pill button */}
      {!open && (
        <button className="apcw-pill" onClick={() => setOpen(true)} aria-label="Open support chat">
          <ChatPillIcon size={18} />
          <span>Need some help?</span>
        </button>
      )}
    </div>
  );
}

const styles = `
.apcw-root {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  font-family: 'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ── Collapsed pill ── */
.apcw-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border: none;
  border-radius: 999px;
  background: ${TEAL};
  color: #0d0f14;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 0 20px rgba(49, 220, 186, 0.45);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.apcw-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 0 26px rgba(49, 220, 186, 0.6);
}
.apcw-pill svg { display: block; }

/* ── Expanded window ── */
.apcw-window {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 360px;
  height: 480px;
  display: flex;
  flex-direction: column;
  background: rgba(15, 15, 25, 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(49, 220, 186, 0.2);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
  opacity: 0;
  transform: translateY(16px) scale(0.96);
  transform-origin: bottom right;
  pointer-events: none;
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.apcw-window.apcw-open {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

/* ── Header ── */
.apcw-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: linear-gradient(135deg, rgba(49, 220, 186, 0.28), rgba(49, 220, 186, 0.08));
  border-bottom: 1px solid rgba(49, 220, 186, 0.18);
  flex-shrink: 0;
}
.apcw-header-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(13, 15, 20, 0.5);
  flex-shrink: 0;
}
.apcw-header-title {
  flex: 1;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.apcw-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.apcw-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* ── Messages ── */
.apcw-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.apcw-messages::-webkit-scrollbar { width: 6px; }
.apcw-messages::-webkit-scrollbar-thumb {
  background: rgba(49, 220, 186, 0.25);
  border-radius: 3px;
}
.apcw-msg {
  max-width: 80%;
  padding: 9px 13px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.apcw-user {
  align-self: flex-end;
  background: ${TEAL};
  color: #0d0f14;
  font-weight: 600;
  border-bottom-right-radius: 4px;
}
.apcw-assistant {
  align-self: flex-start;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e8e8ee;
  border-bottom-left-radius: 4px;
}
.apcw-typing-bubble { padding: 12px 14px; }

/* ── Typing dots ── */
.apcw-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.apcw-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${TEAL};
  opacity: 0.5;
  animation: apcw-bounce 1.2s infinite ease-in-out;
}
.apcw-dot:nth-child(2) { animation-delay: 0.18s; }
.apcw-dot:nth-child(3) { animation-delay: 0.36s; }
@keyframes apcw-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* ── Input row ── */
.apcw-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.apcw-input {
  flex: 1;
  padding: 10px 13px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;
}
.apcw-input::placeholder { color: rgba(255, 255, 255, 0.4); }
.apcw-input:focus { border-color: rgba(49, 220, 186, 0.6); }
.apcw-send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: ${TEAL};
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.apcw-send:hover:not(:disabled) { transform: translateY(-1px); }
.apcw-send:disabled { opacity: 0.45; cursor: default; }

/* Lift the widget above the dashboard's fixed bottom nav on mobile
   (nav is 56px + home-bar safe area; 72px matches .ap-workspace reserve) */
@media (max-width: 768px) {
  .apcw-root {
    bottom: calc(72px + env(safe-area-inset-bottom, 0px));
    right: 16px;
  }
}
@media (max-width: 480px) {
  .apcw-window {
    width: calc(100vw - 32px);
    height: 70vh;
  }
}
`;
